import * as React from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { Wordmark } from '@/components/layout/Wordmark';
import { LocaleToggle } from '@/components/LocaleToggle';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormError } from '@/components/common/FormError';
import { OAuthButtons, OrDivider } from '@/components/auth/OAuthButtons';
import { SendAgainButton } from '@/components/invite/SendAgainButton';
import { SignInLinkForm } from '@/components/invite/SignInLinkForm';
import { describeAuthError } from '@/lib/authErrors';
import { linkErrorKey, oauthErrorKey } from '@/lib/authLink';
import { oauthProviders } from '@/lib/config';

interface LocationState {
  from?: { pathname: string };
}

export function SignIn() {
  const { t } = useTranslation();
  const { session, signIn, signOut, linkError, clearLinkError, sendSignInLink, freshLink, clearFreshLink } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  /** A Google / Apple start refused before any redirect (translated). */
  const [oauthError, setOauthError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  /** 'password' (the default), the sign-in link form, or its sent state for an address. */
  const [mode, setMode] = React.useState<{ kind: 'password' } | { kind: 'link' } | { kind: 'sent'; email: string }>({
    kind: 'password',
  });

  const stateFrom = (location.state as LocationState | null)?.from?.pathname ?? null;
  // An OAuth return carries the `from` it kept across the round trip (router state does not survive it).
  // Pinned: the fresh mark is cleared right after this render, and <Navigate> navigates again whenever
  // its `to` changes, so the destination must not fall back to the board once the mark is gone.
  const oauthFrom = React.useRef<string | null>(null);
  const oauthReturn = freshLink?.via === 'oauth';
  if (oauthReturn && oauthFrom.current === null) oauthFrom.current = freshLink.from ?? stateFrom ?? '/dashboard';
  const from = oauthFrom.current ?? freshLink?.from ?? stateFrom ?? '/dashboard';
  // Back from a sign-in link (or from another account's link or Google account, refused): say who is
  // signed in before going on, never straight to the board. Back from Google or Apple the provider's
  // account chooser already asked "is this you?": straight on. A session simply there goes on as before.
  const confirm = session !== null && (freshLink?.via === 'link' || linkError?.code === 'other_account');
  const hasOAuth = Object.values(oauthProviders()).some(Boolean);

  React.useEffect(() => {
    if (oauthReturn) clearFreshLink();
  }, [oauthReturn, clearFreshLink]);

  if (session && !confirm) return <Navigate to={from} replace />;

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const { error: message } = await signIn(email, password);
    setBusy(false);
    if (!message) return;
    // GoTrue says "Invalid login credentials" for a wrong password AND for an unknown address, on
    // purpose: telling them apart tells an attacker which addresses exist. Keep that property. Any
    // other answer is GoTrue's English for developers: logged, never rendered.
    console.warn('sign-in refused', message);
    setError(t(`auth.errors.${describeAuthError({ message }) ?? 'generic'}`));
  }

  function showLink() {
    setError(null);
    setMode({ kind: 'link' });
  }

  return (
    <div className="grid min-h-full place-items-center bg-canvas p-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <Wordmark tone="canvas" />
          <LocaleToggle />
        </div>

        <Card>
          <CardContent className="p-8">
            {session && confirm ? (
              <LinkSignedIn
                email={session.user.email ?? ''}
                conflict={
                  linkError?.code === 'other_account'
                    ? t(`auth.errors.${linkError.via === 'oauth' ? 'otherAccountOAuth' : 'otherAccount'}`, { email: linkError.email })
                    : null
                }
                onContinue={() => {
                  clearFreshLink();
                  clearLinkError();
                  navigate(from, { replace: true });
                }}
                onSignOut={async () => {
                  // Sign out FIRST: clearing the marks while the session is still there would read as
                  // "signed in, nothing to confirm" and redirect to the board. SIGNED_OUT clears freshLink.
                  await signOut();
                  clearLinkError();
                }}
              />
            ) : (
            <>
            <h1 className="text-2xl font-semibold text-ink">
              {mode.kind === 'password' ? t('auth.title') : t('auth.link.title')}
            </h1>
            <p className="mt-1 text-base text-ink-muted">{t('auth.subtitle')}</p>

            {linkError && mode.kind !== 'sent' && (
              <FormError
                className="mt-6"
                message={t(`auth.errors.${linkError.via === 'oauth' ? oauthErrorKey(linkError) : linkErrorKey(linkError.code)}`, {
                  email: linkError.email,
                })}
                onDismiss={clearLinkError}
              />
            )}

            {hasOAuth && mode.kind !== 'sent' && (
              <div className="mt-6 flex flex-col gap-4">
                <FormError message={oauthError} onDismiss={() => setOauthError(null)} />
                <OAuthButtons
                  redirectPath="/signin"
                  from={stateFrom}
                  onError={(key) => setOauthError(t(`auth.errors.${key}`))}
                />
                <OrDivider />
              </div>
            )}

            {mode.kind === 'link' && (
              <div className="mt-6 flex flex-col gap-4">
                <p className="text-base text-ink-secondary">{t('auth.link.body')}</p>
                <SignInLinkForm
                  idPrefix="signin-link"
                  redirectPath="/signin"
                  createUser={false}
                  email={email}
                  onEmailChange={setEmail}
                  onSent={(address) => {
                    clearLinkError();
                    setMode({ kind: 'sent', email: address });
                  }}
                />
              </div>
            )}

            {mode.kind === 'sent' && (
              <div className="mt-6 flex flex-col gap-4">
                <p role="status" className="text-base text-ink">
                  {t('auth.link.sent', { email: mode.email })}
                </p>
                <SendAgainButton
                  onSend={async () => (await sendSignInLink(mode.email, '/signin', { createUser: false })).error}
                />
              </div>
            )}

            {mode.kind !== 'password' && (
              <Button
                variant="ghost"
                className="mt-4 self-start"
                onClick={() => setMode({ kind: 'password' })}
              >
                {t('auth.link.usePassword')}
              </Button>
            )}

            {mode.kind === 'password' && (
            <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  placeholder={t('auth.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {error && (
                <p role="alert" className="text-base text-danger">
                  {error}
                </p>
              )}

              <Button type="submit" disabled={busy}>
                {busy ? t('auth.submitting') : t('auth.submit')}
              </Button>

              <Button type="button" variant="ghost" onClick={showLink}>
                {t('auth.link.toggle')}
              </Button>
            </form>
            )}

            {/* self-signup: the door of a new agency's free trial. */}
            <p className="mt-6 text-base text-ink-secondary">
              {t('auth.startTrial')}{' '}
              <Link to="/signup" className="font-medium text-accent-base underline-offset-4 hover:underline">
                {t('auth.startTrialLink')}
              </Link>
            </p>
            </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** Fresh from a sign-in link: whose account this is, and the way out if it is not hers. */
function LinkSignedIn({
  email,
  conflict,
  onContinue,
  onSignOut,
}: {
  email: string;
  /** Another account's link was refused while this one stayed signed in (translated). */
  conflict: string | null;
  onContinue(): void;
  onSignOut(): Promise<void>;
}) {
  const { t } = useTranslation();
  const ref = React.useRef<HTMLHeadingElement>(null);
  React.useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <section className="flex flex-col gap-6">
      <h1 ref={ref} tabIndex={-1} className="min-w-0 break-words text-2xl font-semibold text-ink focus:outline-none">
        {t('auth.linkSignedIn.title', { email })}
      </h1>
      {conflict && <FormError message={conflict} />}
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button className="sm:flex-1" onClick={onContinue}>
          {t('auth.linkSignedIn.continue')}
        </Button>
        <Button variant="ghost" onClick={() => void onSignOut()}>
          {t('auth.linkSignedIn.notYou')}
        </Button>
      </div>
    </section>
  );
}
